import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Collapse, Divider, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreImport from "./PreImport.js";
import PostImport from "./PostImport.js";
import useApi from "../../hooks/useApi.js";
import MidImport from "./MidImport.js";
import ImportLayout from "../../components/LayoutWithHeader.js";
import BetaPill from "../../components/BetaPill.js";
import Loading from "../../components/Loading.js";
import { useParams } from "react-router-dom";
import { IMPORT_SOURCES } from "./importSources.js";
import NotFoundPage from "../../routes/NotFoundPage.js";
import { ACHIEVEMENTS_KEY } from "../../utils/cacheUtils.js";

export default function Import() {
  const queryClient = useQueryClient();
  const { fetchImportStatus, fetchImportSummary } = useApi();
  const { source } = useParams();
  const sourceConfig = IMPORT_SOURCES[source];
  // Re-import defaults to collapsed so it doesn't compete with the primary
  // "View imported items" CTA on the completed screen.
  const [reimportExpanded, setReimportExpanded] = useState(false);

  const { data: importSummaryData, isLoading } = useQuery({
    queryKey: ["importSummary", source],
    queryFn: () => fetchImportSummary(source),
    refetchOnWindowFocus: true,
  });

  const { data: importStatusData } = useQuery({
    queryKey: ["importStatus", source],
    queryFn: () => fetchImportStatus(source),
    enabled: importSummaryData?.status === "in_progress",
    refetchInterval: 5000,
    onError: () => {
      toast.error("Lost connection while checking import status", {
        autoClose: false,
      });
    },
  });

  // const importSummaryData = {
  //   status: "completed",
  // };

  // const importStatusData = {
  //   status: "processing_shelves",
  // };

  // const isLoading = false;

  useEffect(() => {
    if (["completed", "failed"].includes(importStatusData?.status)) {
      queryClient.invalidateQueries(["importSummary"]);
      // Badges the import's finishes earned
      queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
    }
  }, [importStatusData?.status, queryClient]);

  if (!sourceConfig) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return <Loading />;
  }

  const isInProgress = importSummaryData?.status === "in_progress";
  const isCompleted =
    importSummaryData?.status === "completed" ||
    importSummaryData?.status === "completed_with_errors";
  const isFailed = importSummaryData?.status === "failed";

  const title =
    isInProgress && importStatusData
      ? `${sourceConfig.label} import in progress`
      : isCompleted
        ? `${sourceConfig.label} import completed`
        : `Import from ${sourceConfig.label}`;

  const isPreImportScreen = !isInProgress && !isCompleted;

  return (
    <ImportLayout
      title={title}
      titleAdornment={
        isPreImportScreen && sourceConfig.beta ? <BetaPill /> : null
      }
      showBackButton={true}
    >
      {isInProgress && importStatusData ? (
        <MidImport data={importStatusData} sourceConfig={sourceConfig} />
      ) : isCompleted ? (
        <>
          <PostImport
            importData={importSummaryData}
            sourceConfig={sourceConfig}
          />
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                Re-import from {sourceConfig.label}
              </Typography>
              <IconButton
                onClick={() => setReimportExpanded((prev) => !prev)}
                size="small"
                aria-expanded={reimportExpanded}
                aria-label="Expand re-import section"
              >
                <ExpandMoreIcon
                  sx={{
                    transform: reimportExpanded
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
                />
              </IconButton>
            </Box>
            <Collapse in={reimportExpanded} sx={{ mt: 1 }}>
              <PreImport
                isFailed={false}
                source={source}
                sourceConfig={sourceConfig}
              />
            </Collapse>
          </Box>
        </>
      ) : (
        <PreImport
          isFailed={isFailed}
          errorMessage={importSummaryData?.summary?.error}
          source={source}
          sourceConfig={sourceConfig}
        />
      )}
    </ImportLayout>
  );
}
